<template>
  <NText variant="caption1" weight="bold" class="pr-4">
    {{ t('common.recentRequests') }}
  </NText>
  <div
    class="bg-neutral-secondary p-4 max-h-[27rem] rounded-md mt-1 flex flex-col"
  >
    <NTable
      ref="loadMoreRef"
      :data="withdrawals"
      cell-padding="small"
      :column-splitter="false"
      :striped="false"
      fixed-header
      identifier="id"
      class="mb-2 mt-1 w-[25rem] basis-[fit-content]"
    >
      <template #hoveredElements="{ row }">
        <NegarTrashLightIcon
          v-if="
            REQUEST_STATUS_ID.open.includes(row.requestStateId) &&
            !row.isPayfast
          "
          ref="deleteWithdrawalButtonRef"
          size="20"
          class="text-critical-primary ml-2 cursor-pointer"
          @click="handleOpenDeleteDialog(row, $event.target)"
        />
      </template>

      <NTableColumn v-slot="{ row }">
        <NegarInfoWithBorderIcon
          size="20"
          class="cursor-pointer"
          @click="openWithdrawalHistoryInfoDialog(row)"
        />
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="`${t('common.amount')} (${t('misc.rial')})`"
        prop="requestWithdrawalValue"
        sortable
        align="text-right"
      >
        <NumberFormatter #="{ number }" :value="row.requestWithdrawalValue">
          <NText variant="caption2">
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="`${t('modules.order.time')} ${t('withdrawal.submitRequest')}`"
        prop="requestEntryDate"
        sortable
        align="text-right"
      >
        <NText variant="caption2" class="align-middle">
          {{ d(row.requestEntryDate, 'shortDate') }} ,
        </NText>
        <NText variant="caption2" class="align-middle ms-1">
          {{ d(row.requestEntryDate, 'shortTime') }}
        </NText>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('common.state')"
        prop="requestState"
        sortable
        align="text-right"
      >
        <NText
          variant="caption2"
          weight="medium"
          :class="{
            'text-brand-primary': REQUEST_STATUS_ID.done.includes(
              row.requestStateId
            ),
            'text-critical-primary': REQUEST_STATUS_ID.failed.includes(
              row.requestStateId
            ),
            'text-neutral-primary': REQUEST_STATUS_ID.canceled.includes(
              row.requestStateId
            ),
          }"
        >
          {{
            row.requestState !== t('withdrawal.accountingDocument')
              ? row.requestState
              : t('withdrawal.requested')
          }}
        </NText>
      </NTableColumn>
    </NTable>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import {
  CASH_QUERY_KEYS,
  REQUEST_STATUS_ID,
  useDeleteWithdrawalQuery,
  useGetWithdrawalInfiniteLiveQuery,
} from '@core/cash';
import { WithdrawalItem } from '@libs/api-reference';
import { useI18n } from 'vue-i18n';
import { NegarTrashLightIcon, NegarInfoWithBorderIcon } from '@negar/icons';
import { useToast } from '@negar/core';
import { useQueryClient } from '@libs/use';
import { sleep } from '@libs/utils';

import { useCashDialogs } from '@/modules/cash';

import NumberFormatter from '@/components/Number.vue';

const { withdrawals, loadMoreRef } = useGetWithdrawalInfiniteLiveQuery();

const { t, d } = useI18n();
const toast = useToast();

const { openWithdrawalDeleteDialog, openWithdrawalHistoryInfoDialog } =
  useCashDialogs();

const queryClient = useQueryClient();

const { deleteWithdrawal, isLoadingDeleteWithdrawal } =
  useDeleteWithdrawalQuery({
    async onSuccess() {
      toast.neutral({
        message: t('withdrawal.deleteDoneMessage'),
        duration: 3000,
      });

      await sleep(3000);

      queryClient.invalidateQueries(CASH_QUERY_KEYS.payableAmounts);
      queryClient.invalidateQueries(CASH_QUERY_KEYS.cash);
      queryClient.invalidateQueries(CASH_QUERY_KEYS.withdrawals);
    },
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });

const handleOpenDeleteDialog = (
  withdrawal: WithdrawalItem,
  deleteWithdrawalButtonRef: HTMLElement
) => {
  openWithdrawalDeleteDialog({
    withdrawal,
    isLoadingDeleteWithdrawal,
    deleteWithdrawal,
    deleteWithdrawalButtonRef,
  });
};
</script>
